import BaseApi from '@core/api/model';

import { toDate } from '@utils/date';

import i18n from '@plugins/i18n';

import Valuation from '@/models/Valuation';
import { Collection } from '@/models';

export class ProductApi extends BaseApi {
    static resource = 'products';

    get endpoints() {
        return {
            ...super.endpoints,
            resyncPricing: this.resyncPricing.bind(this),
            getValuations: this.getValuations.bind(this),
            getValuation: this.getValuation.bind(this),
            postValuation: this.postValuation.bind(this),
            patchValuation: this.patchValuation.bind(this),
            removeValuation: this.removeValuation.bind(this)
        };
    }

    _filterResponseData(data) {
        data = super._filterResponseData(data);

        if (Array.isArray(data)) {
            data = data.map(item => this._filterResponseData(item));
        } else if (Array.isArray(data.listings)) {
            data.listings = data.listings.map(listing => {
                listing.product_id = data.id;
                return listing;
            });
        }

        return data;
    }

    _filterValuationResponseData(data) {
        data = super._filterResponseData(data);

        if (Array.isArray(data)) {
            data = data.map(item => this._filterValuationResponseData(item));
        }

        if (data.instrument && data.instrument.product && data.instrument.product.id) {
            data.product_id = data.instrument.product.id;
            data.product = data.instrument.product;
        }

        if (data.instrument && data.instrument.exchange && data.instrument.exchange.id) {
            data.exchange_id = data.instrument.exchange.id;
            data.exchange = data.instrument.exchange;
        }

        data.name = [
            data.effective_at && toDate(data.effective_at),
            data.origin && i18n.t(`enums.valuation_origin.${data.origin}`),
            data.type && i18n.t(`enums.valuation_type.${data.type}`),
            i18n.t('valuation')
        ]
            .filter(Boolean)
            .join(' ');

        return data;
    }

    async resyncPricing(offerId) {
        const response = await BaseApi.api.post(`${this.base_url}/${offerId}/resync-pricing`);
        return this._respond(response, () => {
            this._updateItemInCollections(offerId, response.data);
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
        });
    }

    async getValuations(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        let extract = BaseApi.extractFilter(filters, 'product_id');

        if (!extract.value) {
            throw new Error('Missing product_id on GET valuations');
        }

        const response = await this.index(offset, limit, sort_by, extract.filters, {
            ...config,
            base_url: `${this.base_url}/${extract.value}/valuations`
        });

        return this._respond(response, () => {
            response.data = this._filterValuationResponseData(response.data);
            this._insertModels(response.data, Valuation);
        });
    }

    async getValuation(valuation_id, product_id, params = {}, options = {}) {
        const response = await BaseApi.api.get(
            `${this.base_url}/${product_id}/valuations/${valuation_id}`,
            params,
            options
        );

        return this._respond(response, () => {
            response.data = this._filterValuationResponseData(response.data);
            Collection.updateItemInCollections(valuation_id, response.data);
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL, Valuation);
        });
    }

    async postValuation(payload = {}) {
        if (!payload.product_id) {
            throw new Error('Missing product_id on POST valuation', payload);
        }

        const response = await BaseApi.api.post(`${this.base_url}/${payload.product_id}/valuations`, payload);

        return this._respond(response, () => {
            response.data = this._filterValuationResponseData(response.data);
            this._clearCollection('Valuation');
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL, Valuation);
        });
    }

    async patchValuation(valuation_id, product_id, payload = {}) {
        if (!valuation_id) {
            throw new Error('Missing valuation_id on PATCH valuation', payload);
        }

        if (!product_id) {
            throw new Error('Missing product_id on PATCH valuation', payload);
        }

        const response = await BaseApi.api.patch(`${this.base_url}/${product_id}/valuations/${valuation_id}`, payload);

        return this._respond(response, () => {
            response.data = this._filterValuationResponseData(response.data);
            Collection.updateItemInCollections(valuation_id, response.data);
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL, Valuation);
        });
    }

    async removeValuation(valuation_id, product_id) {
        if (!valuation_id) {
            throw new Error('Missing valuation_id on DELETE valuation');
        }
        if (!product_id) {
            throw new Error('Missing product_id on DELETE valuation');
        }

        const response = await BaseApi.api.remove(`${this.base_url}/${product_id}/valuations/${valuation_id}`);

        return this._respond(response, () => {
            this._removeItemInCollections(valuation_id);
            Valuation.delete(valuation_id);
        });
    }
}

export default ProductApi;
